import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import Head from '../layout/head/Head'
import Content from '../layout/content/Content'
import { useTranslation } from 'react-i18next'
import { useDSsummaryTablesData, useDataStoreActions } from '../store/dataStore'
import CONSTANTS from '../utils/Constants'
import McPageHelpers, { REQUEST_STATUSES } from './mc-components/McPageHelpers'
import { MarketCalculusCard } from '../components/preview/Preview'
import { getPayloadBySearchParams, useSearchDataStoreActions, useSDSsearchParams } from '../store/searchDataStore'
import McPageHeader from '../components/page-header/McPageHeader'
import McPdfExport from './mc-components/McPdfExport'
import McSubTab from '../components/sub-tab/McSubTab'
import { OVERVIEW_TAB_TYPE } from './mc-components/market-overview/MarketOverviewTable/MarketOverviewTable.settings'
import { TabContent, TabPane, Row, Col, ButtonGroup, Button } from 'reactstrap'
import ChangeSection from './mc-components/market-overview/ChangeSection'
import { usePDFselectedComponentIds } from '../store/pdfStore'
import SummarySection from './mc-components/summary-tables/SummarySection'
import { useRawStoreActions, useRAWcomtradeSummary } from '../store/rawStore'
import { MarketResultServices, RESPONSE_OBJ_NAMES as RON } from '../services/McDataServices'
import ButtonDatePicker from '../components/datePicker/ButtonDatePicker'
import McDataProcessor from './mc-components/McDataProcessor'
import { useEndpointStatusData } from '../store/endpointStatus'

const SummaryView = forwardRef((props, ref) => {
  const { t } = useTranslation()
  const [activeAltTab, setActiveAltTab] = useState(OVERVIEW_TAB_TYPE.SOURCE)

  const comtradeSummaryEndpointStatus = useEndpointStatusData.comtradeSummary()

  const { setSummaryTablesSectionData } = useDataStoreActions()

  const { setRawBykey } = useRawStoreActions()
  const comtradeSummary = useRAWcomtradeSummary()

  const { setSearchParams } = useSearchDataStoreActions()
  const searchParams = useSDSsearchParams()
  const summaryParams = searchParams?.summary
  const isMonthly = summaryParams?.yearly === 'false'

  const setSummaryParam = (key, val) => {
    if (summaryParams?.[key] !== val) {
      const newState = {
        ...summaryParams,
        [key]: val
      }
      setSearchParams('summary', newState)
    }
  }

  const setDateSummaryParam = (value, isMonthly) => {
    if (isMonthly) {
      summaryParams?.yearMonth !== value && setSearchParams('summary', {
        ...summaryParams,
        yearMonth: value
      })
    } else {
      summaryParams?.year !== value && setSearchParams('summary', {
        ...summaryParams,
        year: value
      })
    }
  }

  const summaryTablesData = useDSsummaryTablesData()

  const triggerSummaryChange = (date, monthly) => {
    const year = date.getFullYear()
    const month = date.getMonth() + 1// +1 for backend dates
    const yearMonth = monthly
      ? `${year}${('0' + month.toString()).slice(-2)}`
      : summaryParams.yearMonth

    const newSearchParams = {
      ...searchParams,
      summary: monthly
        ? {
            ...summaryParams,
            yearMonth
          }
        : {
            ...summaryParams,
            year
          }
    }

    const payload = getPayloadBySearchParams(newSearchParams)

    setDateSummaryParam(monthly ? yearMonth : year, monthly)

    MarketResultServices.ComTradeSummary(payload).then(data => {
      McDataProcessor.generateAndSetSummaryTablesSectionData(data, setSummaryTablesSectionData)
      setRawBykey(RON.COMTRADE_SUMMARY, data)
    })
  }

  const initDateByData = (summaryTablesDataP, summaryParamsP) => {
    if (summaryTablesDataP?.import?.month && summaryTablesDataP?.import?.year && !summaryParamsP?.yearMonth) {
      setSearchParams('summary', {
        ...summaryParamsP,
        year: summaryTablesDataP.import.year,
        yearMonth: summaryTablesDataP.import.month,
        yearly: 'true'
      })
    }
  }

  useEffect(() => {
    initDateByData(summaryTablesData, summaryParams)
  }, [summaryTablesData, summaryParams])

  const selectedComponentIds = usePDFselectedComponentIds()

  const tableImportYearRef = useRef(null)
  const tableImportMonthRef = useRef(null)
  const tableExportYearRef = useRef(null)
  const tableExportMonthRef = useRef(null)
  const searchHeader = searchParams.hsCode + ' - ' + McPageHelpers.findCountryName(searchParams.countryToExport) + ' - ' + McPageHelpers.findCountryName(searchParams.yourCountry)
  const tableWorldImportYearRef = useRef(null)
  const tableWorldImportMonthRef = useRef(null)
  const tableWorldExportYearRef = useRef(null)
  const tableWorldExportMonthRef = useRef(null)
  const changeTablesRef = useRef(null)
  const subtitle = (t('world') + ' ' + searchParams.hsCode)

  const pdfDownload = (onlyContent = false) => {
    const tableImportYear = tableImportYearRef?.current?.generateSummaryTablePdf('tableImportYear')
    const tableImportMonth = tableImportMonthRef?.current?.generateSummaryTablePdf('tableImportMonth')
    const tableExportYear = tableExportYearRef?.current?.generateSummaryTablePdf('tableExportYear')
    const tableExportMonth = tableExportMonthRef?.current?.generateSummaryTablePdf('tableExportMonth')
    const tableWorldImportYear = tableWorldImportYearRef?.current?.generateSummaryTablePdf('tableWorldImportYear')
    const tableWorldImportMonth = tableWorldImportMonthRef?.current?.generateSummaryTablePdf('tableWorldImportMonth')
    const tableWorldExportYear = tableWorldExportYearRef?.current?.generateSummaryTablePdf('tableWorldExportYear')
    const tableWorldExportMonth = tableWorldExportMonthRef?.current?.generateSummaryTablePdf('tableWorldExportMonth')
    const changeTables = changeTablesRef?.current?.generateTablesPdf()
    return McPdfExport.SummaryTables(onlyContent, searchHeader, subtitle, selectedComponentIds, changeTables, tableImportYear, tableImportMonth, tableExportYear, tableExportMonth
      , tableWorldImportYear, tableWorldImportMonth, tableWorldExportYear, tableWorldExportMonth)
  }

  useImperativeHandle(ref, () => ({
    async getPdfContent () {
      const content = await pdfDownload(true).then(content => content)
      return content
    }
  }))

  return (
    <>
      <Head title={t('menu.summary_tables')}/>
      <Content>
        <McPageHeader title={t('menu.summary_tables')}/>
        {searchParams.countryToExport && searchParams.yourCountry &&
          <MarketCalculusCard className={'mb-4'}>
            <Row style={{ justifyContent: 'center' }}>
              <Col xs='0' lg='4'>{/* SPACE */}</Col>

              <Col xs='12' lg='4' style={{ alignContent: 'center', justifyContent: 'end' }}>
                <McSubTab
                  activeTab={activeAltTab}
                  setActiveTab={setActiveAltTab}
                />
              </Col>

              <Col xs='12' sm='8' md='6' lg='4' className='mt-1 mt-lg-0'>
                <Row style={{ justifyContent: 'end' }}>
                  <Col xs='6' lg='4' style={{ alignContent: 'center' }}>
                    {summaryParams?.yearMonth && summaryParams?.year &&
                      <ButtonDatePicker
                        selected={
                          isMonthly
                            ? new Date(summaryParams?.yearMonth.toString().substring(0, 4), (parseInt(summaryParams?.yearMonth) - 1/* -1 for backend dates */).toString().substring(4))
                            : new Date(summaryParams?.year.toString())
                        }
                        onChange={(date) => triggerSummaryChange(date, isMonthly)}
                        dateFormat={isMonthly ? 'MM/yyyy' : 'yyyy'}
                        showMonthYearPicker={isMonthly}
                        showFullMonthYearPicker={isMonthly}
                        showYearPicker={!isMonthly}
                        maxDate={new Date()}
                        minDate={new Date('2017')}
                        loading={comtradeSummaryEndpointStatus === REQUEST_STATUSES.REQUESTED}
                      />
                    }
                  </Col>
                  <Col xs='6' lg='6' style={{ alignContent: 'center' }}>
                    <ButtonGroup className="btn-block">
                      <Button
                        size='sm'
                        color="primary"
                        className="btn-dim"
                        onClick={() => { setSummaryParam('yearly', 'true') }}
                        outline={isMonthly}
                      >{t('yearly')}</Button>
                      <Button
                        size='sm'
                        color="primary"
                        className="btn-dim"
                        onClick={() => { setSummaryParam('yearly', 'false') }}
                        outline={!isMonthly}
                      >{t('monthly')}</Button>
                    </ButtonGroup>
                  </Col>
                </Row>
              </Col>
            </Row>
          </MarketCalculusCard>
        }
        <TabContent activeTab={activeAltTab}>
            <TabPane tabId={OVERVIEW_TAB_TYPE.SOURCE}>
              {isMonthly
                ? <MarketCalculusCard className={!comtradeSummary?.importerMonthly?.length ? 'loading-div' : ''}>
                    <SummarySection
                      ref={tableImportMonthRef}
                      data={summaryTablesData?.import?.monthly}
                      date={summaryTablesData?.import?.month}
                      titleKey={'st_month_importer'}
                      graphCode={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_2.replace('T-', 'H-')}
                      code={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_2}
                      isWorld={false}
                      isYearly={false}
                      isImport={true}
                      explanation={summaryTablesData?.import?.monthlyExplanation}
                    />
                  </MarketCalculusCard>
                : <MarketCalculusCard className={!comtradeSummary?.importerYearly?.length ? 'loading-div' : ''}>
                    <SummarySection
                      ref={tableImportYearRef}
                      data={summaryTablesData?.import?.yearly}
                      date={summaryTablesData?.import?.year}
                      titleKey={'st_year_importer'}
                      graphCode={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_1.replace('T-', 'H-')}
                      code={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_1}
                      isWorld={false}
                      isYearly={true}
                      isImport={true}
                      explanation={summaryTablesData?.import?.yearlyExplanation}
                    />
                  </MarketCalculusCard>
              }
            </TabPane>
            <TabPane tabId={OVERVIEW_TAB_TYPE.DESTINATION}>
              {isMonthly
                ? <MarketCalculusCard className={!comtradeSummary?.exporterMonthly?.length ? 'loading-div' : ''}>
                    <SummarySection
                      ref={tableExportMonthRef}
                      data={summaryTablesData?.export.monthly}
                      date={summaryTablesData?.export.month}
                      titleKey={'st_month_exporter'}
                      graphCode={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_4.replace('T-', 'H-')}
                      code={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_4}
                      isWorld={false}
                      isYearly={false}
                      isImport={false}
                      explanation={summaryTablesData?.export.monthlyExplanation}
                    />
                  </MarketCalculusCard>
                : <MarketCalculusCard className={!comtradeSummary?.exporterYearly?.length ? 'loading-div' : ''}>
                    <SummarySection
                      ref={tableExportYearRef}
                      data={summaryTablesData?.export.yearly}
                      date={summaryTablesData?.export.year}
                      titleKey={'st_year_exporter'}
                      graphCode={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_3.replace('T-', 'H-')}
                      code={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_3}
                      isWorld={false}
                      isYearly={true}
                      isImport={false}
                      explanation={summaryTablesData?.export.yearlyExplanation}
                    />
                  </MarketCalculusCard>
              }
            </TabPane>
            <TabPane tabId={OVERVIEW_TAB_TYPE.WORLD}>
              {isMonthly
                ? <>
                    <MarketCalculusCard className={!comtradeSummary?.worldImportMonthly?.length ? 'loading-div' : ''}>
                      <SummarySection
                        ref={tableWorldImportMonthRef}
                        data={summaryTablesData?.world.import.monthly}
                        date={summaryTablesData?.world.import.month}
                        titleKey={'st_world_month_importer'}
                        graphCode={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_6.replace('T-', 'H-')}
                        code={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_6}
                        isWorld={true}
                        isYearly={false}
                        isImport={true}
                        explanation={summaryTablesData?.world.import.monthlyExplanation}
                      />
                    </MarketCalculusCard>
                    <MarketCalculusCard className={!comtradeSummary?.worldExportMonthly?.length ? 'loading-div' : ''}>
                      <SummarySection
                        ref={tableWorldExportMonthRef}
                        data={summaryTablesData?.world.export.monthly}
                        date={summaryTablesData?.world.export.month}
                        titleKey={'st_world_month_exporter'}
                        graphCode={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_8.replace('T-', 'H-')}
                        code={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_8}
                        isWorld={true}
                        isYearly={false}
                        isImport={false}
                        explanation={summaryTablesData?.world.export.monthlyExplanation}
                      />
                    </MarketCalculusCard>
                  </>
                : <>
                    <MarketCalculusCard className={!comtradeSummary?.worldImportYearly?.length ? 'loading-div' : ''}>
                      <SummarySection
                        ref={tableWorldImportYearRef}
                        data={summaryTablesData?.world.import.yearly}
                        date={summaryTablesData?.world.import.year}
                        titleKey={'st_world_year_importer'}
                        graphCode={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_5.replace('T-', 'H-')}
                        code={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_5}
                        isWorld={true}
                        isYearly={true}
                        isImport={true}
                        explanation={summaryTablesData?.world.import.yearlyExplanation}
                      />
                    </MarketCalculusCard>
                    <MarketCalculusCard className={!comtradeSummary?.worldExportYearly?.length ? 'loading-div' : ''}>
                      <SummarySection
                        ref={tableWorldExportYearRef}
                        data={summaryTablesData?.world.export.yearly}
                        date={summaryTablesData?.world.export.year}
                        titleKey={'st_world_year_exporter'}
                        graphCode={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_7.replace('T-', 'H-')}
                        code={CONSTANTS.BADGE_CODES.SUMMARY_TABLES_TABLE_7}
                        isWorld={true}
                        isYearly={true}
                        isImport={false}
                        explanation={summaryTablesData?.world.export.yearlyExplanation}
                      />
                    </MarketCalculusCard>
                  </>
              }
            </TabPane>
            <TabPane tabId={OVERVIEW_TAB_TYPE.CHANGE}>
              <ChangeSection ref={changeTablesRef} />
            </TabPane>
        </TabContent>
      </Content>
    </>
  )
})

export default SummaryView
